import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';
import { loadStripe } from '@stripe/stripe-js';
import config from '../config';

// Initialize Stripe
let stripePromise = null;

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(config?.stripe?.publishableKey);
  }
  return stripePromise;
};

/**
 * Service layer for Stripe payment integration
 * Handles checkout sessions, customer portal, and payment processing
 */

export const stripeService = {
  /**
   * Get Stripe instance
   * @returns {Promise<Stripe>}
   */
  async getStripeInstance() {
    return await getStripe();
  },

  /**
   * Health check for Stripe Edge Function
   * @returns {Promise<{healthy, error}>}
   */
  async checkEdgeFunctionHealth() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { healthy: false, error: 'Not authenticated' };
      }

      // Try to invoke the Edge Function with a test request
      // This will fail fast if STRIPE_SECRET_KEY is not configured
      const { error } = await supabase?.functions?.invoke('create-stripe-checkout', {
        body: {
          planType: 'premium',
          billingInterval: 'monthly',
          customerInfo: {
            userId: user?.id,
            email: user?.email,
            firstName: 'Test',
            lastName: 'User',
            stripeCustomerId: null
          },
          successUrl: `${window.location?.origin}/payment-confirmation`,
          cancelUrl: `${window.location?.origin}/subscription-management`,
          healthCheck: true // Flag to indicate this is a health check
        }
      });

      if (error) {
        // Check for specific configuration errors
        if (error?.message?.includes('STRIPE_NOT_CONFIGURED') || error?.code === 'STRIPE_NOT_CONFIGURED') {
          logger?.error('Stripe Edge Function not configured', error, { requestId });
          return { 
            healthy: false, 
            error: 'Stripe payment system is not configured. Please contact support at support@mybodytoday1.com' 
          };
        }
        
        logger?.error('Edge Function health check failed', error, { requestId });
        return { healthy: false, error: error?.message || 'Edge Function unavailable' };
      }

      return { healthy: true, error: null };
    } catch (error) {
      logger?.error('Health check error', error, { requestId });
      return { healthy: false, error: 'Failed to check Edge Function status' };
    }
  },

  /**
   * Create Stripe checkout session for subscription upgrade
   * @param {string} planType - Plan type (premium, family)
   * @param {string} billingInterval - Billing interval (monthly, yearly)
   * @returns {Promise<{data, error}>}
   */
  async createCheckoutSession(planType, billingInterval = 'monthly') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get user profile for customer info
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('first_name, last_name, email')
        ?.eq('id', user?.id)
        ?.single();

      // Get current subscription for stripe_customer_id
      const { data: subscription } = await supabase
        ?.from('user_subscriptions')
        ?.select('stripe_customer_id')
        ?.eq('user_id', user?.id)
        ?.single();

      // Call Edge Function to create checkout session
      const { data, error } = await supabase?.functions?.invoke('create-stripe-checkout', {
        body: {
          planType,
          billingInterval,
          customerInfo: {
            userId: user?.id,
            email: profile?.email || user?.email,
            firstName: profile?.first_name || '',
            lastName: profile?.last_name || '',
            stripeCustomerId: subscription?.stripe_customer_id || null
          },
          successUrl: `${window.location?.origin}/payment-confirmation`,
          cancelUrl: `${window.location?.origin}/subscription-management`
        }
      });

      if (error) {
        logger?.error('Failed to create checkout session', error, { requestId, planType });
        return { data: null, error };
      }

      logger?.debug('Checkout session created', { requestId, sessionId: data?.sessionId });
      return { data, error: null };
    } catch (error) {
      logger?.error('Checkout session creation error', error, { requestId });
      return { data: null, error: { message: 'Failed to create checkout session' } };
    }
  },

  /**
   * Redirect to Stripe checkout
   * @param {string} sessionId - Stripe checkout session ID
   * @returns {Promise<{error}>}
   */
  async redirectToCheckout(sessionId) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const stripe = await getStripe();
      const { error } = await stripe?.redirectToCheckout({ sessionId });

      if (error) {
        logger?.error('Redirect to checkout failed', error, { requestId, sessionId });
        return { error };
      }

      return { error: null };
    } catch (error) {
      logger?.error('Checkout redirect error', error, { requestId });
      return { error: { message: 'Failed to redirect to checkout' } };
    }
  },

  /**
   * Create Stripe customer portal session
   * @returns {Promise<{data, error}>}
   */
  async createPortalSession() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get current subscription for stripe_customer_id
      const { data: subscription } = await supabase
        ?.from('user_subscriptions')
        ?.select('stripe_customer_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!subscription?.stripe_customer_id) {
        return { data: null, error: { message: 'No active Stripe subscription found' } };
      }

      // Call Edge Function to create portal session
      const { data, error } = await supabase?.functions?.invoke('create-stripe-portal', {
        body: {
          customerId: subscription?.stripe_customer_id,
          returnUrl: `${window.location?.origin}/subscription-management`
        }
      });

      if (error) {
        logger?.error('Failed to create portal session', error, { requestId });
        return { data: null, error };
      }

      logger?.debug('Portal session created', { requestId });
      return { data, error: null };
    } catch (error) {
      logger?.error('Portal session creation error', error, { requestId });
      return { data: null, error: { message: 'Failed to create portal session' } };
    }
  },

  /**
   * Redirect to Stripe customer portal
   * @param {string} portalUrl - Stripe portal URL
   */
  redirectToPortal(portalUrl) {
    window.location.href = portalUrl;
  }
};

export default stripeService;